'use client';

import Link from 'next/link';
import {
  Calendar,
  CreditCard,
  Users,
  BarChart3,
  Clock,
  Shield,
  ArrowRight,
  Check,
} from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useScrollReveal } from '@/app/hooks/use-scroll-reveal';
import { VideoPlayer } from '@/app/components/ui/video-player';

const FEATURES = [
  {
    icon: Calendar,
    title: 'Real-time bookings',
    desc: 'Members browse availability and book instantly. No phone calls, no waiting.',
  },
  {
    icon: CreditCard,
    title: 'Stripe payments',
    desc: 'Secure online payments built in. Members pay at booking, centres get paid automatically.',
  },
  {
    icon: Users,
    title: 'Member self-service',
    desc: 'Members manage their own bookings, memberships, and payment history from one dashboard.',
  },
  {
    icon: BarChart3,
    title: 'Centre analytics',
    desc: 'See occupancy, revenue, and member trends. Make data-driven decisions about your facilities.',
  },
  {
    icon: Clock,
    title: 'Flexible scheduling',
    desc: 'Per-slot pricing, recurring bookings, peak/off-peak rules. Configure to match your centre.',
  },
  {
    icon: Shield,
    title: 'GDPR-compliant',
    desc: 'Data hosted in EU. Role-based access control. Built for Irish data protection requirements.',
  },
] as const;

const HOW_IT_WORKS = [
  { num: '1', title: 'Sign up your centre', desc: 'We set up your facilities, pricing, and branding in SpaceNest.' },
  { num: '2', title: 'Members discover & book', desc: 'Your community finds your centre, browses availability, and books online.' },
  { num: '3', title: 'You manage everything', desc: 'Approve bookings, track payments, and view reports — all in one place.' },
] as const;

const HIGHLIGHTS = [
  'Real-time availability',
  'Online payments via Stripe',
  'Member self-service portal',
  'Role-based staff access',
  'Activity-based pricing',
  'Automated notifications',
] as const;

export function SpaceNestPage() {
  const prefersReducedMotion = useReducedMotion();
  const { ref: featuresRef, isVisible: featuresVisible } = useScrollReveal();
  const { ref: howRef, isVisible: howVisible } = useScrollReveal();
  const { ref: adminRef, isVisible: adminVisible } = useScrollReveal();
  const { ref: salesRef, isVisible: salesVisible } = useScrollReveal();

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-sandron-ink-900 pb-20 pt-[clamp(96px,12vw,160px)]">
        <div
          className="pointer-events-none absolute -right-[200px] -top-[200px] h-[600px] w-[600px] rounded-full blur-[80px]"
          style={{ background: 'radial-gradient(circle, rgba(31,107,68,0.3) 0%, transparent 70%)' }}
          aria-hidden="true"
        />
        <div className="relative z-10 mx-auto max-w-[1200px] px-5 md:px-10 lg:px-0">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3.5 py-1.5">
            <span className="flex h-6 w-6 items-center justify-center rounded bg-spacenest-primary text-xs font-bold text-white">
              S
            </span>
            <span className="text-sm font-medium text-sandron-ink-100">SpaceNest</span>
          </div>

          <h1 className="mt-6 max-w-[800px] font-display text-[clamp(40px,5.5vw,80px)] font-extrabold leading-[1.04] tracking-[-0.025em] text-white [text-wrap:balance]">
            Book community spaces across Ireland.
          </h1>

          <p className="mt-6 max-w-[600px] text-[clamp(17px,1.4vw,20px)] leading-[1.6] text-sandron-ink-300">
            SpaceNest helps community centres, schools, and council facilities
            manage bookings, memberships, and payments. Schools can open up
            their facilities outside term hours and during holidays for
            community use. Available across all 26 counties.
          </p>

          {/* Highlights */}
          <div className="mt-8 flex flex-wrap gap-3">
            {HIGHLIGHTS.map((h) => (
              <span
                key={h}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.06] px-3 py-1.5 text-[13px] font-medium text-sandron-ink-100"
              >
                <Check size={12} className="text-[#7BD2A2]" />
                {h}
              </span>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              href="/contact"
              className="group inline-flex h-14 items-center gap-2.5 rounded-full bg-white px-7 font-display text-base font-semibold text-sandron-ink-900 transition-all duration-200 hover:-translate-y-px hover:bg-sandron-ink-50"
            >
              Get started
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <Link
              href="/contact?topic=spacenest"
              className="inline-flex h-14 items-center rounded-full border border-white/20 px-7 font-display text-base font-semibold text-white transition-all duration-200 hover:border-white/40 hover:bg-white/[0.06]"
            >
              Request a demo
            </Link>
          </div>
        </div>
      </section>

      {/* Member video — see it in action */}
      <section className="bg-white py-[clamp(64px,8vw,112px)]">
        <div className="mx-auto max-w-[1080px] px-5 md:px-10 lg:px-0">
          <div className="mb-8 max-w-[640px] md:mb-12">
            <div className="flex items-center gap-3">
              <span className="h-px w-6 bg-sandron-ink-300" aria-hidden="true" />
              <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-sandron-ink-500">
                See it in action
              </span>
            </div>
            <h2 className="mt-4 font-display text-[clamp(28px,3.5vw,44px)] font-bold leading-[1.12] tracking-[-0.02em] text-sandron-ink-900">
              From browse to confirmed booking — under a minute.
            </h2>
          </div>
          <VideoPlayer
            src="/videos/spacenest-member.mp4"
            title="SpaceNest — member booking flow"
            caption="Find a centre, pick a slot, pay securely. Mobile, tablet, or desktop."
          />
        </div>
      </section>

      {/* Features grid */}
      <section className="py-[clamp(80px,10vw,140px)]">
        <div ref={featuresRef} className="mx-auto max-w-[1200px] px-5 md:px-10 lg:px-0">
          <div className="mb-16 max-w-[720px]">
            <div className="flex items-center gap-3">
              <span className="h-px w-6 bg-sandron-ink-300" aria-hidden="true" />
              <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-sandron-ink-500">
                Features
              </span>
            </div>
            <h2 className="mt-4 font-display text-[clamp(32px,4vw,56px)] font-bold leading-[1.1] tracking-[-0.02em] text-sandron-ink-900">
              Everything your centre needs, in one platform.
            </h2>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 24 }}
                animate={featuresVisible ? { opacity: 1, y: 0 } : { opacity: 0 }}
                transition={{
                  duration: prefersReducedMotion ? 0.15 : 0.6,
                  delay: i * 0.09,
                  ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
                }}
                className="rounded-[20px] border border-sandron-ink-100 bg-white p-6 lg:p-8"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[rgba(31,107,68,0.08)] text-spacenest-primary">
                  <f.icon size={24} strokeWidth={2} />
                </div>
                <h3 className="mt-5 font-display text-xl font-bold text-sandron-ink-900">
                  {f.title}
                </h3>
                <p className="mt-2 text-[15px] leading-[1.6] text-sandron-ink-700">
                  {f.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Admin demo video */}
      <section ref={adminRef} className="bg-sandron-ink-900 py-[clamp(80px,10vw,140px)] text-white">
        <div className="mx-auto max-w-[1080px] px-5 md:px-10 lg:px-0">
          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
            animate={adminVisible ? { opacity: 1, y: 0 } : { opacity: 0 }}
            transition={{
              duration: prefersReducedMotion ? 0.15 : 0.6,
              ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
            }}
            className="mb-8 max-w-[640px] md:mb-12"
          >
            <div className="flex items-center gap-3">
              <span className="h-px w-6 bg-[#7BD2A2]" aria-hidden="true" />
              <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-[#7BD2A2]">
                For centre managers
              </span>
            </div>
            <h2 className="mt-4 font-display text-[clamp(28px,3.5vw,44px)] font-bold leading-[1.12] tracking-[-0.02em] text-white">
              Run your centre from one screen.
            </h2>
            <p className="mt-4 max-w-[560px] text-base leading-[1.6] text-sandron-ink-300">
              The admin dashboard: facility management, booking approvals,
              revenue tracking, and utilisation insights — all in one place.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 24 }}
            animate={adminVisible ? { opacity: 1, y: 0 } : { opacity: 0 }}
            transition={{
              duration: prefersReducedMotion ? 0.15 : 0.7,
              delay: 0.15,
              ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
            }}
          >
            <VideoPlayer
              src="/videos/spacenest-admin.mp4"
              title="SpaceNest admin dashboard demo"
              caption="A 75-second tour of the admin experience."
              variant="dark"
            />
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-sandron-ink-50 py-[clamp(80px,10vw,140px)]">
        <div ref={howRef} className="mx-auto max-w-[1200px] px-5 md:px-10 lg:px-0">
          <div className="mb-16 max-w-[720px]">
            <div className="flex items-center gap-3">
              <span className="h-px w-6 bg-sandron-ink-300" aria-hidden="true" />
              <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-sandron-ink-500">
                How it works
              </span>
            </div>
            <h2 className="mt-4 font-display text-[clamp(32px,4vw,56px)] font-bold leading-[1.1] tracking-[-0.02em] text-sandron-ink-900">
              Three steps to get your centre online.
            </h2>
          </div>

          <div className="grid gap-8 md:grid-cols-3 md:gap-12">
            {HOW_IT_WORKS.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
                animate={howVisible ? { opacity: 1, y: 0 } : { opacity: 0 }}
                transition={{
                  duration: prefersReducedMotion ? 0.15 : 0.6,
                  delay: i * 0.12,
                  ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
                }}
              >
                <span className="font-display text-5xl font-extrabold text-sandron-ink-100">
                  {step.num}
                </span>
                <h3 className="mt-4 font-display text-xl font-bold text-sandron-ink-900">
                  {step.title}
                </h3>
                <p className="mt-2 text-[15px] leading-[1.6] text-sandron-ink-700">
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sales pitch video — the business case */}
      <section ref={salesRef} className="bg-white py-[clamp(80px,10vw,140px)]">
        <div className="mx-auto max-w-[1080px] px-5 md:px-10 lg:px-0">
          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
            animate={salesVisible ? { opacity: 1, y: 0 } : { opacity: 0 }}
            transition={{
              duration: prefersReducedMotion ? 0.15 : 0.6,
              ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
            }}
            className="mb-8 max-w-[640px] md:mb-12"
          >
            <div className="flex items-center gap-3">
              <span className="h-px w-6 bg-sandron-ink-300" aria-hidden="true" />
              <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-sandron-ink-500">
                The business case
              </span>
            </div>
            <h2 className="mt-4 font-display text-[clamp(28px,3.5vw,44px)] font-bold leading-[1.12] tracking-[-0.02em] text-sandron-ink-900">
              Stop losing hours to manual bookings.
            </h2>
            <p className="mt-4 max-w-[560px] text-base leading-[1.6] text-sandron-ink-700">
              90 seconds on the problem, the solution, and what changes when
              SpaceNest goes live at your centre.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 24 }}
            animate={salesVisible ? { opacity: 1, y: 0 } : { opacity: 0 }}
            transition={{
              duration: prefersReducedMotion ? 0.15 : 0.7,
              delay: 0.15,
              ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
            }}
          >
            <VideoPlayer
              src="/videos/spacenest-sales.mp4"
              title="SpaceNest — the business case for community centres"
              caption="Why centres choose SpaceNest. GDPR-compliant, EU-hosted, Stripe-secured."
            />
          </motion.div>
        </div>
      </section>

      {/* CTA band */}
      <section className="relative overflow-hidden bg-spacenest-primary py-[clamp(80px,10vw,120px)]">
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-30 blur-[80px]"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)' }}
          aria-hidden="true"
        />
        <div className="relative z-10 mx-auto max-w-[700px] px-5 text-center">
          <h2 className="font-display text-[clamp(32px,4.5vw,48px)] font-bold leading-[1.1] tracking-[-0.02em] text-white">
            Ready to bring your centre online?
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-white/80">
            We&apos;ll set up your centre, train your team, and have you taking
            bookings within a week.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              href="/contact?topic=spacenest"
              className="inline-flex h-14 items-center gap-2.5 rounded-full bg-white px-7 font-display text-base font-semibold text-spacenest-primary transition-all duration-200 hover:-translate-y-px hover:shadow-lg"
            >
              Talk to us <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
